import React, { useContext, useMemo } from 'react';

import { useParams } from '@thd-olt-component-react/router';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { RentalRedirector } from '@thd-nucleus/app-render';

import { Col, Row } from '@thd-olt-component-react/core-ui';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  ProductInformation,
  RentalDeliveryFormProviders,
  DeliveryForm
} from '@thd-olt-component-react/rental-reservation-form';

import { SkipForNowButton } from '../components/SkipForNowButton';
import { ProductMetadata } from '../components/ProductMetadata';
import { getConfigLists, useFeatureCheck } from '../helpers';

import '../styles/pages/Reservation.scss';

export const Delivery = () => {
  const { channel, segment = '', isDebugMode } = useContext(ExperienceContext);
  const localizedStore = useStore({ online: false });

  const { itemId } = useParams();
  const isMobile = channel === 'mobile';

  const {
    pricingGraphQlEnabled = false,
    rentalClearTools = [],
  } = getConfigLists();

  const isPricingGraphQlEnabled = useFeatureCheck(pricingGraphQlEnabled, localizedStore?.storeId?.padStart(4, '0'));
  const showClearBullet = useMemo(() => rentalClearTools.includes(itemId), [rentalClearTools, itemId]);

  const handleContinueDeliveryButtonClick = () => {
    if (itemId && typeof window !== 'undefined') {
      window.location.href = '/checkout/toolrental';
    }
  };

  return (
    <QueryProvider
      defaultVariables={{ dataSource: 'catalog', fetchPolicy: 'cache-first', skipKPF: true }}
      cacheKey={`rental-reservations-${isMobile ? 'mobile' : 'desktop'}`}
    >
      <Row className="isBound">
        <ErrorBoundary name="redirector">
          <RentalRedirector />
        </ErrorBoundary>
        <ErrorBoundary name="product-metadata">
          <ProductMetadata itemId={itemId} pageType="reservations" />
        </ErrorBoundary>
        <ErrorBoundary name="breadcrumbs">
          <Col sm="10">
            <Breadcrumbs
              itemId={itemId}
              showInternet={isMobile}
            />
          </Col>
          { !isMobile && (
            <Col sm="2" className="skip-button">
              <SkipForNowButton itemId={itemId} />
            </Col>
          )}
        </ErrorBoundary>
        <ErrorBoundary name="rental-reservation-form" className="rental-reservation-form">
          <Row>
            <Col sm="5">
              <ProductInformation
                itemId={itemId}
                pricingGraphQlEnabled={isPricingGraphQlEnabled}
                isRentalClearTool={showClearBullet}
                isDeliveryFlow
              />
            </Col>
            <Col sm="7" className="right-card">
              <RentalDeliveryFormProviders
                rentalEstimateEnabled
                contractTermsEnabled
                damageProtectionEnabled
                graphQlEnabled
                itemId={itemId}
              >
                <DeliveryForm itemId={itemId} handleButtonClick={handleContinueDeliveryButtonClick} />
              </RentalDeliveryFormProviders>
            </Col>
          </Row>
        </ErrorBoundary>
      </Row>
    </QueryProvider>
  );
}

Delivery.displayName = 'DeliveryPage';
Delivery.propTypes = {};
Delivery.defaultProps = {};
